/**
 * Persist trip DAG to InstantDB. Call from main thread only.
 * Batches transact() to avoid SQLite "too-many-arguments" when trip has many nodes/edges.
 */

import { db } from '../instant';
import { id } from '@instantdb/react';
import { buildTripDagFromRouteStructure } from './tripDag';
import type { RouteStructure } from './routeInference';

/** Smaller batches to avoid transaction timeout when writing many nodes/edges. */
const MAX_TRANSACT_BATCH = 100;

/**
 * Run transactions in batches. Each "unit" (e.g. all deletes, one node, one edge) stays in a
 * single transaction; we only split when a unit exceeds MAX_TRANSACT_BATCH or when accumulating
 * units would exceed it.
 */
async function transactByUnits(units: unknown[][]): Promise<void> {
    let batch: unknown[] = [];
    let batchSize = 0;

    const flush = async () => {
        if (batch.length === 0) return;
        await db.transact(batch as Parameters<typeof db.transact>[0]);
        batch = [];
        batchSize = 0;
    };

    for (const unit of units) {
        if (unit.length > MAX_TRANSACT_BATCH) {
            await flush();
            for (let i = 0; i < unit.length; i += MAX_TRANSACT_BATCH) {
                const chunk = unit.slice(i, i + MAX_TRANSACT_BATCH);
                await db.transact(chunk as Parameters<typeof db.transact>[0]);
            }
            continue;
        }
        if (batchSize + unit.length > MAX_TRANSACT_BATCH && batch.length > 0) {
            await flush();
        }
        batch.push(...unit);
        batchSize += unit.length;
    }
    await flush();
}

/** Existing node/edge entities from a query (need id for delete). */
export interface ExistingTripDagNode {
    id: string;
}
export interface ExistingTripDagEdge {
    id: string;
}

/**
 * Rebuild and persist the trip DAG: delete existing nodes/edges for the trip,
 * insert new ones from route structure. Caller (tripRebuildTrigger) sets trips.planRecalculatedAt and planInputsFingerprint.
 */
export async function rebuildTripDag(
    tripId: string,
    existingNodes: ExistingTripDagNode[],
    existingEdges: ExistingTripDagEdge[],
    routeStructure: RouteStructure,
    sourceStorageLocationId: string,
    destStorageLocationId: string
): Promise<void> {
    const dag = buildTripDagFromRouteStructure(routeStructure, sourceStorageLocationId, destStorageLocationId);

    const deleteOps: unknown[] = [];
    for (const node of existingNodes) {
        deleteOps.push(db.tx.tripDagNodes[node.id].delete());
    }
    for (const edge of existingEdges) {
        deleteOps.push(db.tx.tripDagEdges[edge.id].delete());
    }

    const nodeUnits: unknown[][] = dag.nodes.map(node => {
        const nodeId = id();
        return [
            db.tx.tripDagNodes[nodeId].update({
                index: node.index,
                kind: node.kind,
                eventId: node.eventId ?? undefined,
                storageLocationId: node.storageLocationId ?? undefined,
            }),
            db.tx.tripDagNodes[nodeId].link({ trip: tripId }),
        ];
    });

    const edgeUnits: unknown[][] = dag.edges.map(edge => {
        const edgeId = id();
        return [
            db.tx.tripDagEdges[edgeId].update({
                fromNodeIndex: edge.fromNodeIndex,
                toNodeIndex: edge.toNodeIndex,
            }),
            db.tx.tripDagEdges[edgeId].link({ trip: tripId }),
        ];
    });

    await transactByUnits([deleteOps, ...nodeUnits, ...edgeUnits]);
}
